import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Box, IconButton, Collapse, Tooltip } from "@material-ui/core";
import { ThreeDots } from "react-loader-spinner";
import {
  CheckCircle,
  Lock,
  PlayArrow,
  ExpandMore,
  ExpandLess,
  Schedule,
  DoneAll,
  Undo,
} from "@material-ui/icons";
import SchoolIcon from "@material-ui/icons/School";
import Button from "components/CustomButtons/Button";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardIcon from "components/Card/CardIcon";
import CardBody from "components/Card/CardBody";
import FeedbackButton from "components/CTSGuide/FeedbackButton";
import { useTranslation } from "react-i18next";
import { client } from "services/axiosClient";
import ctsGuidePortalStyle from "assets/jss/material-dashboard-pro-react/views/ctsGuidePortalStyle";
import useThemeColors from "hooks/useThemeColors";
import CTSMiniNav from "components/CTSGuide/CTSMiniNav";

const useStyles = makeStyles(ctsGuidePortalStyle);

export default function Methodology() {
  const classes = useStyles();
  const { t } = useTranslation();
  const { clientColor } = useThemeColors();
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState({
    completed_modules: [],
    methodology_progress_percentage: 0,
  });
  const [loading, setLoading] = useState(true);
  const [expandedModule, setExpandedModule] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await client.get("/api/ctsguide/methodology-data/");

      setModules(res.data.modules || []);
      setProgress({
        completed_modules: res.data.progress?.completed_modules || [],
        methodology_progress_percentage:
          res.data.progress?.methodology_progress_percentage || 0,
      });
    } catch (error) {
      // Error loading methodology
    } finally {
      setLoading(false);
    }
  };

  const handleModuleToggle = (moduleSlug) => {
    setExpandedModule(expandedModule === moduleSlug ? null : moduleSlug);
  };

  const handleCompleteModule = async (moduleSlug) => {
    try {
      const updatedCompleted = progress.completed_modules.includes(moduleSlug)
        ? progress.completed_modules.filter((s) => s !== moduleSlug)
        : [...progress.completed_modules, moduleSlug];

      await client.post("/api/ctsguide/user-progress/", {
        completed_modules: updatedCompleted,
      });

      setProgress((prev) => ({
        ...prev,
        completed_modules: updatedCompleted,
        methodology_progress_percentage: Math.round(
          (updatedCompleted.length / modules.length) * 100,
        ),
      }));
    } catch (error) {
      // Error updating progress
    }
  };

  const handleCompleteAllModules = async () => {
    try {
      const allModuleSlugs = modules.map((m) => m.slug);

      await client.post("/api/ctsguide/user-progress/", {
        completed_modules: allModuleSlugs,
      });

      setProgress((prev) => ({
        ...prev,
        completed_modules: allModuleSlugs,
        methodology_progress_percentage: 100,
      }));
    } catch (error) {
      // Error completing all modules
    }
  };

  const isModuleCompleted = (moduleSlug) =>
    progress.completed_modules.includes(moduleSlug);

  const isModuleLocked = (index) => {
    if (index === 0) return false;
    const previousModule = modules[index - 1];
    return !isModuleCompleted(previousModule?.slug);
  };

  const allModulesCompleted =
    modules.length > 0 && modules.every((m) => isModuleCompleted(m.slug));

  const getModuleIcon = (moduleSlug, index) => {
    if (isModuleCompleted(moduleSlug)) {
      return <CheckCircle style={{ color: "#4caf50", fontSize: "1.2em" }} />;
    }
    if (isModuleLocked(index)) {
      return <Lock style={{ color: "#bbb", fontSize: "1.2em" }} />;
    }
    return <PlayArrow style={{ color: clientColor, fontSize: "1.2em" }} />;
  };

  return (
    <div className={classes.pageContainer}>
      <Box display="flex" justifyContent="center" mb={0.5}>
        <CTSMiniNav />
      </Box>
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="client" icon>
              <CardIcon color="client">
                <SchoolIcon />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>
                {t("ctsguide.nav.methodology")}
              </h4>
            </CardHeader>
            <CardBody>
              {loading ? (
                <div className={classes.loadingContainer}>
                  <ThreeDots
                    width="120px"
                    color={clientColor}
                    ariaLabel="loading"
                  />
                </div>
              ) : (
                <>
                  {/* Header with action buttons - minimal style */}
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                    mb={2}
                    style={{ gap: "8px" }}
                  >
                    {allModulesCompleted && (
                      <Box
                        display="flex"
                        alignItems="center"
                        style={{ marginRight: "auto" }}
                      >
                        <CheckCircle
                          style={{
                            color: "#4caf50",
                            marginRight: "6px",
                            fontSize: "18px",
                          }}
                        />
                        <span style={{ color: "#4caf50", fontSize: "13px" }}>
                          {t("ctsguide.methodology.all_completed")}
                        </span>
                      </Box>
                    )}

                    {!allModulesCompleted && (
                      <Tooltip title={t("ctsguide.methodology.complete_all")}>
                        <span>
                          <Button
                            size="sm"
                            justIcon
                            color="success"
                            onClick={handleCompleteAllModules}
                          >
                            <DoneAll />
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Box>

                  {/* Modules List */}
                  {modules.map((module, index) => {
                    const completed = isModuleCompleted(module.slug);
                    const locked = isModuleLocked(index);
                    const expanded = expandedModule === module.slug;

                    return (
                      <div
                        key={module.id}
                        className={`${classes.moduleCard} ${
                          completed ? classes.moduleCardCompleted : ""
                        } ${locked ? classes.moduleCardLocked : ""}`}
                        onClick={() =>
                          !locked && handleModuleToggle(module.slug)
                        }
                        style={{ cursor: locked ? "not-allowed" : "pointer" }}
                      >
                        <div className={classes.moduleHeader}>
                          <div className={classes.moduleIcon}>
                            {getModuleIcon(module.slug, index)}
                          </div>
                          <div style={{ flex: 1 }}>
                            <h3 className={classes.moduleTitle}>
                              {module.order}.{" "}
                              {module.title?.[t("lang")] || module.slug}
                            </h3>
                            <div className={classes.moduleTime}>
                              <Schedule
                                fontSize="small"
                                style={{ marginRight: "5px" }}
                              />
                              {module.estimated_time_minutes}{" "}
                              {t("common.minutes")}
                            </div>
                          </div>
                          <div>
                            {!locked && (
                              <IconButton size="small">
                                {expanded ? <ExpandLess /> : <ExpandMore />}
                              </IconButton>
                            )}
                          </div>
                        </div>

                        {/* Module Content */}
                        {!locked && (
                          <Collapse in={expanded}>
                            <Box mt={2} mb={1} style={{ paddingLeft: "36px" }}>
                              <div
                                dangerouslySetInnerHTML={{
                                  __html:
                                    module.content?.[t("lang")] ||
                                    t(
                                      "ctsguide.methodology.content_placeholder",
                                    ),
                                }}
                              />

                              <Box
                                mt={3}
                                display="flex"
                                justifyContent="flex-end"
                              >
                                <Tooltip
                                  title={
                                    completed
                                      ? t(
                                          "ctsguide.methodology.mark_incomplete",
                                        )
                                      : t("ctsguide.methodology.mark_complete")
                                  }
                                >
                                  <span>
                                    <Button
                                      size="sm"
                                      justIcon
                                      color={
                                        completed ? "system_gray" : "success"
                                      }
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCompleteModule(module.slug);
                                      }}
                                    >
                                      {completed ? <Undo /> : <CheckCircle />}
                                    </Button>
                                  </span>
                                </Tooltip>
                              </Box>
                            </Box>
                          </Collapse>
                        )}
                      </div>
                    );
                  })}

                  {modules.length === 0 && (
                    <Box textAlign="center" p={4}>
                      <p>{t("ctsguide.methodology.no_modules")}</p>
                    </Box>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      {/* Floating Feedback Button */}
      <FeedbackButton pageOrigin="methodology" />
    </div>
  );
}
